/**
 * Bundle integrity guard for the Bundles + Free Gift landing pages.
 *
 * The bundles are sold at per-item selling plan prices that are only valid as a
 * set, and they come with free gifts. Two things therefore have to hold on every
 * cart update:
 *
 * 1. QUANTITY SYNC. Shopify propagates the removal of a nested parent to its
 *    children, but not a change of quantity: every child stays at the quantity it
 *    was added with. Ten clicks of add-to-cart followed by dropping the parent back
 *    to one in the cart would leave nine free gifts paid for by nothing. Each child
 *    is held at `_lp_unit x bundles` instead, where `bundles` is the parent's
 *    quantity divided by the parent's own `_lp_unit`.
 *
 *    Which line is the parent comes from Shopify, not from us: /cart.js returns
 *    `parent_relationship.parent_key` on every nested line. Deriving it from our own
 *    line properties instead would have missed every cart filled before this code
 *    shipped — and carts live for weeks. Child lines report
 *    `instructions.can_update_quantity: true`, so moving them is supported.
 *    See https://shopify.dev/docs/apps/build/product-merchandising/nested-cart-lines/create-nested-cart-lines
 *
 * 2. BUNDLE INTEGRITY. Leaving one product behind after removing the others would
 *    sell it at bundle pricing on its own, so a broken-up bundle is cleared out
 *    entirely. Removing a CHILD is not covered by Shopify — testing showed the
 *    behaviour varies with the shape of the cart — so the rule is enforced here
 *    rather than relied upon.
 *
 * How a bundle is recognised: every line carries the properties written by
 * assets/bfg-buy-box.js —
 *
 *   _lp_source = "<landing page key>"            marks the line as ours, groups it
 *   _lp_items  = "<how many paid lines>"         how many paid lines there should be
 *   _lp_unit   = "<qty in one bundle>"           the per-bundle quantity of this line
 *   _lp_group  = "<source>:<parent variant>:<purchase type>"
 *                                                keeps a one-time and a subscription
 *                                                bundle from merging into one line
 *
 * Gifts carry `_free_gift` as well and are not counted towards `_lp_items`; Shopify
 * removes them with their parent, and rule 2 sweeps up any that outlive it. A gift
 * the shopper has declined is simply absent from the cart, so rule 1 never puts one
 * back — it only ever moves lines that are still there.
 *
 * This is the shopper-facing half. It runs in the browser, so it can be bypassed
 * by hitting /cart/change.js directly — the durable guarantee is a Cart and
 * Checkout Validation function, which is not built yet.
 */
(function () {
  'use strict';

  // Printed by reading `window.__bfgBundleGuard` in the console. A build marker
  // rather than `true`, so "is the fix actually live on this page?" is one glance
  // instead of a guess — this file has been debugged remotely more than once.
  const BUILD = 'r10: veil only while writing, interaction lock split out and never deferred';

  if (window.__bfgBundleGuard) return;
  window.__bfgBundleGuard = BUILD;

  // A pass that finds nothing to fix leaves the cart exactly as it read it, so that
  // same cart cannot need fixing again. Without this, anything that counts as a cart
  // update without moving a line — an app rewriting cart attributes, for instance —
  // triggers a full reconcile, several cart reads and a veiled drawer, only to
  // conclude nothing was wrong. Reset to null whenever a pass does change the cart,
  // so the next one always re-verifies from scratch.
  let verifiedFingerprint = null;

  function cartFingerprint(cart) {
    const items = (cart && cart.items) || [];
    // A Shopify line key already encodes the variant and the properties, so key,
    // quantity and parent are the whole of what rules 1 and 2 actually read — every
    // _lp_* property and _free_gift is inside the key.
    //
    // MAINTENANCE: this holds only while the rules read nothing outside the cart
    // lines. A rule keyed on a date window, a metafield or a config flag would make
    // an unchanged cart able to change verdict, and this fingerprint would then be
    // insufficient on its own.
    return items
      .map((i) => `${i.key}:${i.quantity}:${(i.parent_relationship && i.parent_relationship.parent_key) || ''}`)
      .join('|');
  }

  const SOURCE_KEY = '_lp_source';
  const SIZE_KEY = '_lp_items';
  const GIFT_KEY = '_free_gift';
  const UNIT_KEY = '_lp_unit';

  const isTrue = (value) => String(value ?? '').trim().toLowerCase() === 'true';

  /**
   * Decision tracing, off unless asked for. Three ways to turn it on, because the
   * first check now happens on page load: a console-set global is gone by then, so
   * `?bfgdebug=1` and the localStorage flag are the ones that can actually catch it.
   *
   *   localStorage.setItem('bfgGuardDebug', '1')   persists across reloads
   *   ?bfgdebug=1                                  on the URL
   *   window.__bfgBundleGuardDebug = true          same page only
   *
   * The payload is built lazily so tracing costs nothing when it is off.
   */
  function debugEnabled() {
    // Wholly defensive: this runs inside enforce(), so anything that throws here
    // would abort the sync itself — the exact silent failure this exists to explain.
    try {
      if (window.__bfgBundleGuardDebug) return true;
      if (localStorage.getItem('bfgGuardDebug')) return true;
      return new URLSearchParams(window.location.search).has('bfgdebug');
    } catch (error) {
      return false;
    }
  }

  function debug(label, build) {
    try {
      if (debugEnabled()) console.log(`[bfg-bundle-guard] ${label}`, build());
    } catch (error) {
      console.error('bfg-bundle-guard: tracing failed', error);
    }
  }

  /**
   * Endpoint URLs from the theme's own `routes`, which carry the locale prefix.
   *
   * Never build these by appending to a root: the mutation endpoints live under
   * `/cart/` but the read endpoint is `/cart.js` at the shop root, and treating them
   * alike produces `/cart/cart.js`, which 404s. Same `toJsonUrl` shape as
   * assets/norse-cart-ui.js and assets/norse-bundles-buy-box.js.
   */
  function jsUrl(route, fallback) {
    const url = String(route || fallback);
    return /\.js(\?|$)/.test(url) ? url : `${url.replace(/\/+$/, '')}.js`;
  }

  const cartJsUrl = () => jsUrl(window.routes?.cart_url, '/cart');
  const changeJsUrl = () => jsUrl(window.routes?.cart_change_url, '/cart/change');
  const updateJsUrl = () => jsUrl(window.routes?.cart_update_url, '/cart/update');

  async function fetchCart() {
    const response = await fetch(cartJsUrl(), { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`GET ${cartJsUrl()} responded ${response.status}`);
    return response.json();
  }

  /** Defaults to 1 so a line written before `_lp_unit` existed still syncs sensibly. */
  function unitOf(properties) {
    const parsed = parseInt(properties[UNIT_KEY], 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  /* ------------------------------------------------------------ rule 1: sync */

  /** True for a line belonging to a landing-page bundle. */
  const isOurs = (item) => Boolean((item.properties || {})[SOURCE_KEY]);

  /**
   * The cart's bundles, grouped by Shopify's OWN parent pointer.
   *
   * /cart.js returns `parent_relationship.parent_key` on every nested line, so the
   * structure never has to be reconstructed from line properties. That matters: a
   * cart filled before this code shipped has no properties of ours to read, and it
   * is exactly the cart a shopper is mid-session with. Only `_lp_unit` is read from
   * properties, and it defaults to 1 — which is what every landing page configures.
   */
  /**
   * The line a child hangs off, given its `parent_relationship.parent_key`.
   *
   * The exact key is tried first, but it cannot be trusted on its own: a line key is
   * `"<variant id>:<hash>"` and Shopify regenerates the hash whenever the line's
   * characteristics change — "including, but not limited to, properties and discount
   * applications". Changing the parent's quantity changes its discount allocations,
   * which is exactly the moment this code has to work, and `parent_key` is left
   * pointing at a key that no longer exists. So fall back to the variant id, which
   * is stable, narrowed by "not itself nested" and "same bundle" so it cannot latch
   * onto an unrelated line that happens to share the variant.
   * See https://shopify.dev/docs/api/ajax/reference/cart (line item key)
   */
  function resolveParent(parentKey, child, items, byKey) {
    const exact = byKey.get(parentKey);
    if (exact) return exact;

    const variantId = Number(String(parentKey).split(':')[0]);
    if (!Number.isFinite(variantId) || variantId <= 0) return null;

    const source = (child.properties || {})[SOURCE_KEY];
    return (
      items.find(
        (candidate) =>
          candidate.variant_id === variantId &&
          !candidate.parent_relationship?.parent_key &&
          (candidate.properties || {})[SOURCE_KEY] === source
      ) || null
    );
  }

  function bundleGroups(cart) {
    const items = cart.items || [];
    const byKey = new Map();
    items.forEach((item) => {
      if (item.key) byKey.set(item.key, item);
    });

    const groups = new Map();
    items.forEach((item) => {
      const parentKey = item.parent_relationship?.parent_key;
      if (!parentKey || !item.key) return;

      const parent = resolveParent(parentKey, item, items, byKey);
      // A genuinely orphaned child is rule 2's business, not rule 1's.
      if (!parent || parent === item) return;
      // Scoped so nested lines belonging to some other app are left alone.
      if (!isOurs(item) && !isOurs(parent)) return;

      // Grouped on the parent we RESOLVED, not the key the child claims, so a stale
      // pointer and a live one still land in the same group.
      if (!groups.has(parent.key)) groups.set(parent.key, { parent, children: [] });
      groups.get(parent.key).children.push(item);
    });

    return [...groups.values()];
  }

  /**
   * The {id, quantity} changes that bring a group's children back in line with its
   * parent. Empty when the bundle is already consistent.
   */
  function desyncedChanges(group) {
    const parentUnit = unitOf(group.parent.properties || {});

    // Floored: an off-multiple parent quantity entitles the shopper to the bundles
    // they have actually paid for, never to a fraction of one rounded up. Hitting 0
    // leaves the parent alone in the cart, which rule 2 then clears.
    const bundles = Math.floor(group.parent.quantity / parentUnit);

    return group.children
      .map((item) => ({ item, expected: unitOf(item.properties || {}) * bundles }))
      .filter(({ item, expected }) => item.quantity !== expected)
      .map(({ item, expected }) => ({ id: item.key, quantity: expected }));
  }

  /* ------------------------------------------------------- rule 2: integrity */

  /**
   * Groups the cart's landing-page lines and returns the ones whose bundle is no
   * longer whole. Quantity is irrelevant here — a shopper is free to buy two
   * bundles; what matters is that every distinct product is still present.
   */
  function brokenGroups(cart) {
    const groups = new Map();

    (cart.items || []).forEach((item) => {
      const properties = item.properties || {};
      const source = properties[SOURCE_KEY];
      if (!source) return;

      if (!groups.has(source)) {
        groups.set(source, { expected: 0, paid: [], gifts: [] });
      }
      const group = groups.get(source);

      if (isTrue(properties[GIFT_KEY])) {
        group.gifts.push(item);
        return;
      }

      group.paid.push(item);
      // Lines of one bundle all carry the same size; take the largest seen so a
      // stale line from an older configuration cannot lower the bar.
      const expected = parseInt(properties[SIZE_KEY], 10);
      if (Number.isFinite(expected)) {
        group.expected = Math.max(group.expected, expected);
      }
    });

    const broken = [];
    groups.forEach((group, source) => {
      // A gift with no paid line left beside it is the one shape `expected` cannot
      // describe: it is written only by paid lines, so a group of pure gifts reads as
      // "size unknown" and would be waved through. That is not a hypothetical — a
      // bundle whose only paid product is unavailable in the shopper's market adds as
      // the gift alone, and the gift then has nothing paying for it.
      if (!group.paid.length) {
        if (group.gifts.length) broken.push({ source, items: [...group.gifts] });
        return;
      }

      if (!group.expected) return;
      const distinct = new Set(group.paid.map((item) => item.variant_id)).size;
      if (distinct < group.expected) {
        broken.push({ source, items: [...group.paid, ...group.gifts] });
      }
    });
    return broken;
  }

  /* --------------------------------------------------------- cart mutation */

  // Non-zero while the guard is mid-write, so the fetch interception can tell its
  // own traffic from the theme's. A counter, not a boolean: writes are sequential
  // today but a nested pair must not clear the flag early.
  let internalRequests = 0;

  /**
   * The section list to ask /cart/change.js for, so the response carries everything
   * needed to repaint whichever cart surface the shopper is looking at.
   */
  function sectionsBody() {
    const page = document.querySelector('cart-items');
    if (page && typeof page.getSectionsToRender === 'function') {
      try {
        return {
          sections: page.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname,
        };
      } catch (error) {
        // getSectionsToRender() reads the cart page's footer element directly and
        // throws when it is absent. Fall through to the drawer.
      }
    }

    const overlay = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    if (overlay && typeof overlay.getSectionsToRender === 'function') {
      return {
        sections: overlay.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname,
      };
    }

    return {};
  }

  /** POSTs to a cart endpoint, flagged as ours so the fetch hook ignores the echo. */
  function veil(on) {
    const api = window.NorseCartUI;
    if (!api || typeof api.setCartDrawerBusy !== 'function') return;
    try {
      api.setCartDrawerBusy(on);
    } catch (error) {
      /* no drawer on this surface */
    }
  }

  async function cartPost(url, body) {
    internalRequests += 1;
    // The drawer dims only while the cart is genuinely being rewritten. Sequential
    // line-by-line writes stay under one dim thanks to the linger in norse-cart-ui.
    veil(true);
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, ...sectionsBody() }),
      });
      const result = await response.json().catch(() => ({}));
      return { response, result };
    } finally {
      internalRequests -= 1;
      veil(false);
    }
  }

  /**
   * A 200 is not proof a line moved: an accepted-but-ignored write is otherwise
   * indistinguishable from success and would leave the guard reporting a synced cart
   * while the gifts stayed overstocked. Lines asked to go to 0 are expected to
   * vanish, and a successful update can regenerate a key, so a missing line is never
   * treated as a failure — only a present line at the wrong quantity is.
   */
  function unapplied(changes, cart) {
    return changes.filter((change) => {
      if (change.quantity <= 0) return false;
      const landed = (cart.items || []).find((line) => line.key === change.id);
      return landed && landed.quantity !== change.quantity;
    });
  }

  /**
   * Applies {id, quantity} changes and returns the resulting cart, or null when
   * none of them landed.
   *
   * Decreases go out as ONE /cart/update.js — it takes every line in a single
   * request keyed by line item key, which is both faster and far fewer requests than
   * a change.js per line. It is deliberately not used for increases: the docs warn
   * that "the update.js endpoint doesn't validate the quantity on variants that are
   * already in the cart", so raising a line through it can oversell. Raising only
   * happens when the shopper puts the parent UP, which is not the exploit and can
   * afford the slower validated path.
   *
   * `strict` decides what a failure means. A quantity sync that fails has left the
   * cart exploitable, so it throws and lets the caller clear the bundle. A removal
   * that fails has usually already happened — removing a parent cascades to its
   * children, so anything queued behind it is gone — and failing there would abort a
   * clean-up that is nearly done.
   */
  async function applyChanges(changes, { strict = true, currentById = new Map() } = {}) {
    if (!changes.length) return null;

    const raises = changes.some((change) => change.quantity > (currentById.get(change.id) ?? 0));

    if (!raises && changes.length > 1) {
      const updates = {};
      changes.forEach((change) => {
        updates[String(change.id)] = change.quantity;
      });

      const { response, result } = await cartPost(updateJsUrl(), { updates });
      const rejected = !response.ok || Boolean(result.status);
      const missed = rejected ? changes : unapplied(changes, result);

      debug('update.js', () => ({
        sent: updates,
        http: response.status,
        rejected,
        unapplied: missed,
        message: result.description || result.message || null,
      }));

      if (!missed.length) return result;

      const detail = rejected
        ? result.description || result.message || `cart/update.js rejected (${response.status})`
        : `${missed.length} of ${changes.length} lines did not move`;
      console.warn(`bfg-bundle-guard: batched update did not fully apply — ${detail}`);
      if (strict) throw new Error(detail);
      // Not strict: fall through and retry line by line, which is the slow but
      // validated path and may still land some of them.
    }

    let cart = null;

    // Sequentially, because each change reindexes the cart and can cascade to
    // nested children — a parallel batch would race against Shopify's own cleanup.
    for (const change of changes) {
      const { response, result } = await cartPost(changeJsUrl(), change);
      const rejected = !response.ok || Boolean(result.status);

      debug('change.js', () => ({
        sent: change,
        http: response.status,
        rejected,
        message: result.description || result.message || null,
      }));

      if (rejected) {
        if (strict) {
          throw new Error(result.description || result.message || `cart/change.js rejected line ${change.id}`);
        }
        continue;
      }

      if (unapplied([change], result).length) {
        const detail = `line ${change.id} asked for ${change.quantity}, cart reports ${(result.items || []).find((l) => l.key === change.id)?.quantity}`;
        console.warn(`bfg-bundle-guard: change.js accepted but did not apply — ${detail}`);
        if (strict) throw new Error(detail);
        continue;
      }

      cart = result;
    }

    return cart;
  }

  /** Children and gifts first, parents last, so a cascade cannot orphan the queue. */
  function parentLast(items) {
    const rank = (item) => (item.parent_relationship?.parent_key ? 0 : 1);
    return [...items].sort((a, b) => rank(a) - rank(b));
  }

  /* ------------------------------------------------------------- repainting */

  /** Repaints the /cart page from a change.js response. False when not on it. */
  function repaintCartPage(cart) {
    const page = document.querySelector('cart-items');
    if (!page || typeof page.getSectionsToRender !== 'function' || !cart?.sections) return false;

    let sections;
    try {
      sections = page.getSectionsToRender();
    } catch (error) {
      return false;
    }

    // <cart-items> exposes its section list but no renderer, so the sections are
    // patched in the same way assets/cart.js does after its own quantity change.
    sections.forEach((section) => {
      const host = document.getElementById(section.id);
      const html = cart.sections[section.section];
      if (!host || !html) return;
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
      if (!source) return;
      (host.querySelector(section.selector) || host).innerHTML = source.innerHTML;
    });

    const isEmpty = cart.item_count === 0;
    page.classList.toggle('is-empty', isEmpty);
    document.getElementById('main-cart-footer')?.classList.toggle('is-empty', isEmpty);
    if (typeof updateCartUpdateHeaderCount === 'function') updateCartUpdateHeaderCount(cart.item_count);

    return true;
  }

  function repaint(cart) {
    // The cart page first: calling renderContents() there would pop the drawer open
    // over a page the shopper is already reading the cart on.
    if (!repaintCartPage(cart)) {
      const overlay = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (overlay && typeof overlay.renderContents === 'function') overlay.renderContents(cart);
    }

    // Everything else that tracks the cart — header count, mini cart, upsells —
    // listens for this. Published while `running` is still set, so the guard cannot
    // re-enter through its own notification.
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bfg-bundle-guard', cartData: cart });
    }
  }

  /* --------------------------------------------------------------- enforcing */

  let running = false;
  let rerunRequested = false;
  let debounceTimer = null;
  let seededCart = null;
  let busyHeld = false;
  let busyRelease = null;

  /**
   * Extends the cart drawer's own loading state over the reconcile.
   *
   * The theme finishes its request and clears the spinner while the children are
   * still at the old quantity, so the shopper sees the parent settle, gets the cart
   * back, and only then watches the children jump — and can act in that gap.
   * Holding the theme's own busy flag closes it: `.cart-drawer--busy` keeps the
   * spinner up and assets/norse-cart-ui.js already refuses stepper clicks while it
   * is set.
   *
   * `setCartDrawerBusy` is a counter and no-ops without a drawer, so this composes
   * with the theme's own holds and is inert on the cart page. Always released on a
   * timer as well: a stuck spinner over the cart is worse than a late one.
   */
  function holdCart(hold) {
    const api = window.NorseCartUI;
    if (!api || typeof api.setCartDrawerBusy !== 'function') return;
    if (hold === busyHeld) return;

    busyHeld = hold;
    try {
      // Locks input for the whole reconcile but paints nothing: a pass that only
      // reads has no risk to show. cartPost asks for the veil when a write starts.
      api.setCartDrawerBusy(hold, { silent: true });
    } catch (error) {
      busyHeld = false;
      return;
    }

    clearTimeout(busyRelease);
    busyRelease = hold ? setTimeout(() => holdCart(false), 8000) : null;
  }

  /**
   * Coalesces a burst into one run and, where possible, skips the read entirely.
   *
   * `seed` is a cart the caller already has — the theme's own change.js response, for
   * instance, which is the post-mutation cart and exactly what would be re-fetched.
   * Reusing it removes one round trip from the critical path.
   *
   * The delay collapses a shopper clicking the stepper repeatedly into a single
   * reconcile instead of one per click. Kept short: this is the window in which a
   * cart is exploitable, so it trades a barely perceptible pause for far fewer
   * requests, not the other way round.
   */
  function scheduleEnforce(seed, delay = 100, { hold = false } = {}) {
    if (seed) seededCart = seed;
    // Held from the moment it is scheduled, not from when it runs, so the debounce
    // window is covered too — that gap is the one the shopper can act in.
    if (hold) holdCart(true);
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      enforce();
    }, delay);
  }

  async function enforce() {
    // A run already in flight must not be dropped: whatever prompted this call
    // happened after that run read the cart, so it needs its own pass afterwards.
    if (running) {
      rerunRequested = true;
      return;
    }
    running = true;

    try {
      const seed = seededCart;
      seededCart = null;

      let cart = seed || (await fetchCart());
      let changed = false;

      const fingerprint = cartFingerprint(cart);
      if (fingerprint === verifiedFingerprint) {
        debug('skip', () => ({ reason: 'unchanged since last compliant pass' }));
        return;
      }

      // Cleared before the work, not after it, so that ANY abnormal exit from here
      // on — a throw, a rejected write, a reload mid-pass — leaves the cart
      // untrusted and the next trigger re-verifies it from scratch.
      verifiedFingerprint = null;

      // Whether the rules asked for anything, regardless of whether the write then
      // landed. Only a cart that needed NOTHING can be recorded as known-good: a
      // needed change that silently failed to apply must be looked at again.
      let needed = false;

      debug('enforce', () => ({ build: BUILD, itemCount: cart.item_count, sections: sectionsBody() }));

      // Line keys are derived from the variant and the properties, never from the
      // position, so the snapshot's keys stay valid as earlier groups are rewritten.
      const groups = bundleGroups(cart);
      debug('groups', () => ({
        lines: (cart.items || []).map((i) => `${i.title} x${i.quantity} key=${i.key} parent=${i.parent_relationship?.parent_key || '-'}`),
        groups: groups.map((g) => ({
          parent: `${g.parent.key} x${g.parent.quantity}`,
          children: g.children.map((c) => `${c.key} x${c.quantity}`),
          changes: desyncedChanges(g),
        })),
      }));

      for (const group of groups) {
        const changes = desyncedChanges(group);
        if (!changes.length) continue;
        needed = true;

        // Current quantities, so applyChanges can tell a decrease from a raise and
        // pick the batched endpoint only where it is safe.
        const currentById = new Map(group.children.map((item) => [item.key, item.quantity]));

        let updated = null;
        try {
          updated = await applyChanges(changes, { currentById });
        } catch (error) {
          // Shopify refusing to move a nested child would otherwise leave the gifts
          // overstocked, which is the whole thing this guards against. Clearing the
          // bundle is the safe way to fail.
          console.error('bfg-bundle-guard: could not sync bundle quantities, clearing the bundle', error);
          const clear = parentLast([group.parent, ...group.children]);
          updated = await applyChanges(
            clear.map((item) => ({ id: item.key, quantity: 0 })),
            { strict: false }
          );
        }

        if (updated) {
          cart = updated;
          changed = true;
        }
      }

      // Re-read after the sync: zeroing a child is one of the ways a bundle breaks.
      const broken = brokenGroups(cart);
      debug('broken', () => broken.map((g) => ({ source: g.source, lines: g.items.length })));
      const items = parentLast(broken.flatMap((group) => group.items).filter((item) => item.key));
      if (items.length) {
        needed = true;
        const updated = await applyChanges(
          items.map((item) => ({ id: item.key, quantity: 0 })),
          { strict: false }
        );
        if (updated) {
          cart = updated;
          changed = true;
        }
      }

      debug('outcome', () => ({
        changed,
        finalQuantities: (cart.items || []).map((i) => `${i.title} x${i.quantity}`),
      }));

      if (!changed) {
        if (!needed) verifiedFingerprint = fingerprint;
        return;
      }

      repaint(cart);

      if (broken.length) {
        document.dispatchEvent(
          new CustomEvent('norse:bfg-bundle-cleared', {
            bubbles: true,
            detail: { sources: broken.map((group) => group.source) },
          })
        );
      }
    } catch (error) {
      console.error('bfg-bundle-guard: could not enforce bundle integrity', error);
    } finally {
      running = false;
      // Something asked for a pass while this one was mid-flight. Run it now, with no
      // debounce — the delay has already been served. The hold carries over to it;
      // releasing here would flash the spinner off between the two passes.
      if (rerunRequested) {
        rerunRequested = false;
        scheduleEnforce(null, 0);
      } else {
        holdCart(false);
      }
    }
  }

  /**
   * Runs the guard after ANY Shopify cart mutation, whoever made it.
   *
   * The events are not enough on their own. The cart drawer's quantity stepper is
   * handled by assets/norse-cart-ui.js, which posts to /cart/change.js itself and
   * repaints from the response without publishing `cartUpdate` or dispatching
   * `cart:refresh` whenever it cannot hand off to <cart-drawer-items> — and every
   * app or future surface is its own version of that question. Wrapping fetch is
   * how assets/gift-autoadd-extension.js already solves this; same approach here.
   */
  function interceptCartMutations() {
    if (typeof window.fetch !== 'function' || window.fetch.__bfgBundleGuardWrapped) return;

    const originalFetch = window.fetch;

    const isCartMutation = (url, method) => {
      if (String(method || 'GET').toUpperCase() !== 'POST') return false;
      let pathname = '';
      try {
        pathname = new URL(url, window.location.href).pathname;
      } catch (error) {
        pathname = String(url || '').split('?')[0];
      }
      return /\/cart\/(add|change|update)(\.js)?$/.test(pathname);
    };

    window.fetch = async function (...args) {
      const [resource, options] = args;
      // Duck-typed rather than `instanceof Request`: referencing a constructor that
      // may not exist would throw inside a wrapper every cart call goes through.
      const isRequest = typeof resource === 'object' && resource !== null && 'url' in resource;
      const url = typeof resource === 'string' ? resource : isRequest ? resource.url : '';
      const method = isRequest ? resource.method : options?.method;
      const mutating = isCartMutation(url, method);

      // Read before awaiting: by the time the response lands the guard may have
      // moved on, and this has to reflect who made THIS request.
      const ours = internalRequests > 0;

      const response = await originalFetch.apply(this, args);

      // Deferred so the caller gets to repaint from its own response first.
      //
      // Never re-triggered by our own writes. `running` only blocks re-entrancy, not
      // a run scheduled for after it clears — so a cart that cannot be brought into
      // line (Shopify refusing or ignoring a child update) would otherwise have the
      // guard rewriting it, rescheduling itself and hammering /cart/change.js in an
      // endless loop. Same reason assets/gift-autoadd-extension.js tracks its own
      // requests before reacting to cart mutations.
      if (mutating && response.ok && !ours) {
        // The theme's own response IS the post-mutation cart, so it is handed over as
        // the seed and the guard skips its GET /cart.js entirely. Cloned because the
        // caller still has to read the body. A parse failure just means no seed.
        let seed = null;
        try {
          seed = await response.clone().json();
          if (!seed || !Array.isArray(seed.items)) seed = null;
        } catch (error) {
          seed = null;
        }
        scheduleEnforce(seed, 100, { hold: true });
      }

      return response;
    };

    window.fetch.__bfgBundleGuardWrapped = true;
  }

  function start() {
    // All routed through the debouncer: the theme fires several of these for a single
    // shopper action, and they must add up to one reconcile, not four.
    // `hold` on every shopper-driven trigger: each of these follows a cart mutation,
    // so the drawer should stay in its loading state until the children have caught up.
    const trigger = (event) => {
      // Our own repaint publishes this too; reacting to it would cost a pointless pass.
      if (event?.source === 'bfg-bundle-guard') return;
      scheduleEnforce(event?.cartData || null, 100, { hold: true });
    };

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, trigger);
    }
    // Belt and braces: the theme's cart surfaces do not all go through pub/sub.
    document.addEventListener('cart:refresh', () => scheduleEnforce(null, 100, { hold: true }));
    document.addEventListener('cart:updated', () => scheduleEnforce(null, 100, { hold: true }));
    interceptCartMutations();

    // A cart can arrive already out of sync: desynced in an earlier session, on
    // another device, or by a request that never reached this script. Nothing on this
    // page load will mutate it, so the event-driven paths above would all sit idle
    // and the shopper would keep the extra gifts by simply not touching the cart.
    //
    // Skipped when the cart is empty, which is most page views. `is-empty` is
    // server-rendered onto the drawer from `cart == empty`, so reading it here keeps
    // this from adding a /cart.js request to every page in the store.
    if (!document.querySelector('cart-drawer.is-empty')) enforce();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
